import { call } from '@advanza/api'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { FilterValue } from 'components/reports/common/ReportFilter'

export interface Filters {
    start_date?: string
    end_date?: string
    [key: string]: FilterValue
}

export interface Pagination {
    page: number
    limit: number
    total: number
    pages: number
}

export interface FingerprintSignupReportData {
    fingerprint_signup_event_id: number
    service_provider_id: number
    business_name: string
    visitor_id: string
    fingerprint_request_id: string | null
    country_code: string
    created: string | null
    same_visitor_signup_count: number
}

export interface FingerprintSignupSummary {
    total_events: number
    distinct_visitors: number
    visitors_with_multiple_signups: number
    signups_in_period: number
    coverage_rate: number
}

export interface FingerprintSignupReportState {
    data: FingerprintSignupReportData[]
    pages: Record<number, string[]>
    rows: Record<string, FingerprintSignupReportData>
    pagination: Pagination | null
    summary: FingerprintSignupSummary | null
    filters: Filters
    isLoading: boolean
    error: string | null
}

interface ApiResponse {
    items: FingerprintSignupReportData[]
    count: number
    summary: FingerprintSignupSummary
}

export const fetchFingerprintSignupReport = createAsyncThunk(
    'fingerprintSignupReport/fetchData',
    async (
        params: {
            filters: Filters
            page?: number
            limit?: number
            country?: string
        },
        { rejectWithValue },
    ) => {
        try {
            const { filters, page = 0, limit = 50, country } = params

            const queryParams = {
                ...filters,
                page,
                limit,
                ...(country && { country }),
            }

            const response: ApiResponse = await call('office/fingerprint-signup-report', {
                method: 'GET',
                query: queryParams,
            })

            const pageItems = response.items
            const pages: Record<number, string[]> = {}
            const rows: Record<string, FingerprintSignupReportData> = {}

            pages[page] = pageItems.map(
                (item, index) => `${item.fingerprint_signup_event_id}_${index}`,
            )
            pageItems.forEach((item, index) => {
                const id = `${item.fingerprint_signup_event_id}_${index}`
                rows[id] = item
            })

            const totalPages = Math.ceil(response.count / limit)

            return {
                data: response.items,
                pages,
                rows,
                summary: response.summary,
                pagination: {
                    page,
                    limit,
                    total: response.count,
                    pages: totalPages,
                } as Pagination,
            }
        } catch (error) {
            const errorMessage =
                error instanceof Error
                    ? error.message
                    : 'Failed to fetch fingerprint sign-up report'
            return rejectWithValue(errorMessage)
        }
    },
)

const initialState: FingerprintSignupReportState = {
    data: [],
    pages: {},
    rows: {},
    pagination: null,
    summary: null,
    filters: {},
    isLoading: false,
    error: null,
}

const fingerprintSignupReportSlice = createSlice({
    name: 'fingerprintSignupReport',
    initialState,
    reducers: {
        filtersChanged: (state, action: PayloadAction<Filters>) => {
            state.filters = action.payload
        },
        resetState: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchFingerprintSignupReport.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(fetchFingerprintSignupReport.fulfilled, (state, action) => {
                state.isLoading = false
                state.data = action.payload.data
                state.pages = action.payload.pages
                state.rows = action.payload.rows
                state.pagination = action.payload.pagination
                state.summary = action.payload.summary
                state.filters = action.meta.arg.filters
                state.error = null
            })
            .addCase(fetchFingerprintSignupReport.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })
    },
})

export const { filtersChanged, resetState } = fingerprintSignupReportSlice.actions

export default fingerprintSignupReportSlice.reducer
